import { usePage } from '@inertiajs/react';
import { Box, Container, Typography, Grid, Card, CardContent, Button } from '@mui/material';
import {
    People as PeopleIcon,
    PersonSearch as PersonSearchIcon,
    Home as HomeIcon,
    Analytics as AnalyticsIcon,
    List as ListIcon,
    Timeline as TimelineIcon,
    CalendarMonth as CalendarIcon
} from '@mui/icons-material';
import { router } from '@inertiajs/react';

interface Props {
    customers_count: number;
    onboarded_prospects_count: number;
    active_listings_count: number;
    [key: string]: any;
}

export default function AdminHomePage() {
    const { props } = usePage<Props>();
    const { customers_count, onboarded_prospects_count, active_listings_count } = props;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Page Title */}
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 4 }}>
                Admin Dashboard
            </Typography>

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {customers_count}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Customers
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PersonSearchIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {onboarded_prospects_count}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Onboarded Prospects
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <HomeIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                                <Box>
                                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                        {active_listings_count}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Active Listings
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Navigation Buttons */}
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Quick Actions
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Feature Usage
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                View feature usage statistics and analytics
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<AnalyticsIcon />}
                                onClick={() => router.visit('/admin/feature_usage')}
                                sx={{
                                    bgcolor: 'black',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'grey.800'
                                    }
                                }}
                            >
                                View Feature Usage
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Renters
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Manage and view all prospects currently searching
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<PersonSearchIcon />}
                                onClick={() => router.visit('/admin/renters')}
                                sx={{
                                    bgcolor: 'black',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'grey.800'
                                    }
                                }}
                            >
                                View Renters
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Active Listings
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                View all published listings
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<ListIcon />}
                                onClick={() => router.visit('/admin/listings')}
                                sx={{
                                    bgcolor: 'black',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'grey.800'
                                    }
                                }}
                            >
                                View Listings
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                System Logs
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                View system events timeline and activity logs
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<TimelineIcon />}
                                onClick={() => router.visit('/admin/system_logs')}
                                sx={{
                                    bgcolor: 'black',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'grey.800'
                                    }
                                }}
                            >
                                View System Logs
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Tour Bookings
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                View all scheduled and past tour bookings
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                startIcon={<CalendarIcon />}
                                onClick={() => router.visit('/admin/tour_bookings')}
                                sx={{
                                    bgcolor: 'black',
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'grey.800'
                                    }
                                }}
                            >
                                View Tour Bookings
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
}
